/* eslint-disable react/no-danger */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { Link, Element } from 'react-scroll';
import { Box, Typography, CircularProgress } from '@mui/material';
import type { IviewLease } from '../Lease/types';

interface LeaseAbstractionTabProps {
  leaseId: number;
}

function normalizeHtmlContent(htmlContent: string): string {
  const container = document.createElement('div');
  container.innerHTML = htmlContent;
  const boldTags = container.querySelectorAll('b');

  boldTags.forEach((boldTag) => {
    const { nextSibling } = boldTag;
    if (nextSibling && nextSibling.nodeName === 'B') {
      const br = document.createElement('br');
      boldTag.parentNode?.insertBefore(br, nextSibling);
    }
  });

  return container.innerHTML;
}

export default function LeaseAbstractionTab({ leaseId }: LeaseAbstractionTabProps): JSX.Element {
  const { data, isLoading, error } = useQuery(
    ['lease-abstraction-for-panel', leaseId],
    async () => axios({
      url: `api/lease/${leaseId}/`,
      method: 'get',
    }),
    {
      enabled: !!leaseId,
      select: (res: AxiosResponse<IviewLease>) => res.data.detail,
    },
  );

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' py={4}>
        <CircularProgress sx={{ color: '#00CFA1' }} size={32} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box textAlign='center' py={4}>
        <Typography color='error'>Failed to load lease abstraction</Typography>
      </Box>
    );
  }

  const { abstractions } = data.data;
  const { headings } = data;

  if (!abstractions.length) {
    return (
      <Box textAlign='center' py={4}>
        <Typography color='textSecondary'>No lease abstraction data available</Typography>
      </Box>
    );
  }

  return (
    <Box className='lease-abstraction-tab'>
      {/* Headings Sidebar - positioned on right */}
      {headings.length > 0 && (
        <Box className='headings-sidebar'>
          <Box className='headings-inner'>
            {headings.map((heading, idx) => (
              <Link
                key={heading}
                activeClass='active'
                to={heading}
                smooth
                duration={80}
                offset={idx === 0 ? -20 : -10}
                isDynamic
                spy
                containerId='abstraction-scroll-container'
                className='heading-link'
              >
                {heading}
              </Link>
            ))}
          </Box>
        </Box>
      )}

      {/* Content Area - scrollable */}
      <Box className='abstraction-content' id='abstraction-scroll-container'>
        {abstractions.map((abs) => (
          <Element name={abs.title} key={abs.id} className='abstraction-section'>
            <a
              href={abs.pdf_url.startsWith('http') ? abs.pdf_url : `${process.env.REACT_APP_BASE_URL ?? ''}${abs.pdf_url}`}
              target='_blank'
              rel='noreferrer'
              className='section-title'
            >
              {abs.title}
            </a>
            <div
              className='section-content'
              dangerouslySetInnerHTML={{ __html: normalizeHtmlContent(abs.description) }}
            />
          </Element>
        ))}
      </Box>
    </Box>
  );
}
